import { Flex, Tag, Text } from '@spotify-internal/encore-web';
import {
  extractLastNameComponent,
  scaleWeights,
} from '@spotify/confidence-core-react';
import {
  GetWorkflowInstanceQuery,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import { useSetResolvedTitle } from '@spotify/plugin-confidence-projects-react';
import {
  formatWeight,
  parseWorkflowInstanceName,
  useGetWorkflowInstance,
} from '@spotify/plugin-confidence-workflows-react';

import { PreviewShell, StateTag, SummarySection } from './components';

type WorkflowInstance = Extract<
  NonNullable<GetWorkflowInstanceQuery['workflowInstance']>,
  { __typename: 'WorkflowV1WorkflowInstance' }
>;

type ModuleData = WorkflowInstance['moduleData2'];
type AbTestModule = NonNullable<ModuleData['abtest']>;
type FlagsModule = NonNullable<ModuleData['flags']>;
type MetricsModule = NonNullable<ModuleData['metrics']>;

const entityLabel = (entity: { name: string; displayName?: string | null }) =>
  entity.displayName || extractLastNameComponent(entity.name) || entity.name;

const TreatmentList = ({ abtest }: { abtest: AbTestModule }) => {
  const treatments = scaleWeights(
    (abtest.treatments ?? []).map(treatment => ({
      ...treatment,
      weight: Number(treatment.weight ?? 0),
    })),
  );
  if (treatments.length === 0) {
    return (
      <Text variant="bodySmall" semanticColor="textSubdued">
        No treatments.
      </Text>
    );
  }
  return (
    <Flex direction="column" gap="tighter-4">
      {treatments.map((treatment, index) => {
        const variantName = getTypeOrNull(treatment.variant)?.name;
        const label =
          treatment.displayName ||
          treatment.name ||
          (variantName ? extractLastNameComponent(variantName) : undefined) ||
          `Treatment ${index + 1}`;
        return (
          <Flex key={`${label}-${index}`} alignItems="center" gap="tighter-2">
            <Text variant="bodySmall">{label}</Text>
            <Text variant="bodySmall" semanticColor="textSubdued">
              {formatWeight(treatment.weight)}
            </Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

const AudienceSummary = ({ flags }: { flags: FlagsModule }) => {
  const segment = getTypeOrNull(flags.segment);
  const proportionRaw = segment?.allocation?.proportion?.value;
  const proportionNumber = proportionRaw ? Number(proportionRaw) : undefined;
  const allocationPercent =
    proportionNumber !== undefined && Number.isFinite(proportionNumber)
      ? `${Math.round(proportionNumber * 100)}%`
      : undefined;
  const criteriaCount = segment?.targeting?.criteria?.length ?? 0;

  return (
    <Flex direction="column" gap="tighter-2">
      {flags.targetingKeySelector && (
        <Flex alignItems="center" gap="tighter-2">
          <Text variant="bodySmall">Randomized by</Text>
          <Flex wrap="wrap" gap="tighter-2" aria-label="Randomization key">
            <Tag>{flags.targetingKeySelector}</Tag>
          </Flex>
        </Flex>
      )}
      {allocationPercent && (
        <Flex alignItems="center" gap="tighter-2">
          <Text variant="bodySmall">Allocation:</Text>
          <Text variant="bodySmall" semanticColor="textSubdued">
            {allocationPercent}
          </Text>
        </Flex>
      )}
      <Text variant="bodySmall" semanticColor="textSubdued">
        {criteriaCount === 0
          ? 'All users'
          : `${criteriaCount} user criteri${criteriaCount === 1 ? 'on' : 'a'}`}
      </Text>
    </Flex>
  );
};

const isSuccessRole = (
  metricRole: MetricsModule['metrics'][number]['metricRole'],
): boolean => 'successMetricKind' in metricRole;

const MetricList = ({
  title,
  metrics,
}: {
  title: string;
  metrics: ReadonlyArray<MetricsModule['metrics'][number]>;
}) => {
  if (metrics.length === 0) {
    return null;
  }
  return (
    <SummarySection title={title}>
      <Flex wrap="wrap" gap="tighter-2" aria-label={title}>
        {metrics.map((entry, index) => {
          const metric = getTypeOrNull(entry.metric);
          const label =
            metric?.displayName ||
            (metric?.name
              ? extractLastNameComponent(metric.name)
              : undefined) ||
            `Metric ${index + 1}`;
          const id = metric?.name ?? `metric-${index}`;
          return <Tag key={id}>{label}</Tag>;
        })}
      </Flex>
    </SummarySection>
  );
};

interface WorkflowArtifactPreviewProps {
  resourceName: string;
  label: string;
}

export const WorkflowArtifactPreview = ({
  resourceName,
  label,
}: WorkflowArtifactPreviewProps) => {
  const { workflow, id } = parseWorkflowInstanceName(resourceName);
  const { workflowInstance, loading, errorMessage } = useGetWorkflowInstance({
    workflowId: workflow,
    instanceId: id,
  });

  const previewError =
    errorMessage ?? (!loading && !workflowInstance ? 'not-found' : undefined);

  useSetResolvedTitle(
    workflowInstance?.displayName || extractLastNameComponent(resourceName),
  );

  const moduleData = workflowInstance?.moduleData2;
  const abtest = moduleData?.abtest ?? null;
  const flagsModule = moduleData?.flags ?? null;
  const metricsModule = moduleData?.metrics ?? null;
  const hypothesis = moduleData?.hypothesis?.text ?? '';
  const entity = getTypeOrNull(metricsModule?.entity);
  const assignmentTable = getTypeOrNull(metricsModule?.assignmentTable);
  const surfaces = (workflowInstance?.surfaces ?? [])
    .map(surface => getTypeOrNull(surface))
    .filter((surface): surface is NonNullable<typeof surface> =>
      Boolean(surface),
    );
  const successMetrics = (metricsModule?.metrics ?? []).filter(m =>
    isSuccessRole(m.metricRole),
  );
  const guardrailMetrics = (metricsModule?.metrics ?? []).filter(
    m => !isSuccessRole(m.metricRole),
  );

  return (
    <PreviewShell loading={loading} error={previewError}>
      {workflowInstance && (
        <>
          <Flex alignItems="center" gap="tighter-2">
            <Text variant="bodySmall" semanticColor="textSubdued">
              {label}
            </Text>
            <StateTag state={workflowInstance.state} />
          </Flex>
          {hypothesis && (
            <SummarySection title="Hypothesis">
              <Text variant="bodySmall">{hypothesis}</Text>
            </SummarySection>
          )}
          {entity && (
            <SummarySection title="Entity">
              <Flex wrap="wrap" gap="tighter-2" aria-label="Entity">
                <Tag>{entityLabel(entity)}</Tag>
              </Flex>
            </SummarySection>
          )}
          {assignmentTable && (
            <SummarySection title="Assignment table">
              <Flex wrap="wrap" gap="tighter-2" aria-label="Assignment table">
                <Tag>{entityLabel(assignmentTable)}</Tag>
              </Flex>
            </SummarySection>
          )}
          {surfaces.length > 0 && (
            <SummarySection
              title={surfaces.length === 1 ? 'Surface' : 'Surfaces'}
            >
              <Flex wrap="wrap" gap="tighter-2" aria-label="Surfaces">
                {surfaces.map(surface => (
                  <Tag key={surface.name}>{surface.displayName}</Tag>
                ))}
              </Flex>
            </SummarySection>
          )}
          {abtest && (
            <SummarySection title="Treatments">
              <TreatmentList abtest={abtest} />
            </SummarySection>
          )}
          {flagsModule && (
            <SummarySection title="Audience">
              <AudienceSummary flags={flagsModule} />
            </SummarySection>
          )}
          <MetricList title="Success metrics" metrics={successMetrics} />
          <MetricList title="Guardrail metrics" metrics={guardrailMetrics} />
        </>
      )}
    </PreviewShell>
  );
};
